/**
 * API Configuration
 * 
 * Centralized API configuration that reads from environment variables
 * and provides consistent API endpoints across the application.
 */

/**
 * Build the API base URL from environment variables
 * Priority: REACT_APP_API_URL > REACT_APP_HOST + REACT_APP_BACKEND_PORT > localhost:5000
 */
const buildApiBaseUrl = () => {
  // First priority: explicit API URL
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Second priority: host + port from env; else same hostname as the UI (LAN/local)
  const port = process.env.REACT_APP_BACKEND_PORT || '5000';
  let host = process.env.REACT_APP_HOST;
  if (!host && typeof window !== 'undefined' && window.location?.hostname) {
    host = window.location.hostname;
  }
  host = host || 'localhost';

  // Check if host already includes protocol
  if (host.startsWith('http://') || host.startsWith('https://')) {
    return `${host}:${port}/api`;
  }

  return `http://${host}:${port}/api`;
};

// Export the API base URL
export const API_BASE_URL = buildApiBaseUrl();

// Debug logging for development
if (process.env.NODE_ENV === 'development') {
  console.log('🔧 API Configuration:', {
    REACT_APP_API_URL: process.env.REACT_APP_API_URL,
    REACT_APP_HOST: process.env.REACT_APP_HOST,
    REACT_APP_BACKEND_PORT: process.env.REACT_APP_BACKEND_PORT,
    API_BASE_URL
  });
}

// Common API endpoints
export const API_ENDPOINTS = {
  // Authentication endpoints
  auth: {
    login: () => `${API_BASE_URL}/auth/login`,
    logout: () => `${API_BASE_URL}/auth/logout`,
    validateSession: () => `${API_BASE_URL}/auth/validate-session`
  },
  
  // File attachment endpoints
  fileAttachment: {
    byEntity: (entityTypeId, entityId) => `${API_BASE_URL}/file-attachment/entity/${entityTypeId}/${entityId}`,
    download: (fileId) => `${API_BASE_URL}/file-attachment/download/${fileId}`,
    upload: () => `${API_BASE_URL}/file-attachment`,
    delete: (fileId) => `${API_BASE_URL}/file-attachment/${fileId}`
  },
  
  // CAPA endpoints
  capa: {
    list: () => `${API_BASE_URL}/capa`,
    byId: (id) => `${API_BASE_URL}/capa/${id}`,
    fileAttachments: (id) => `${API_BASE_URL}/capa/${id}/file-attachments`,
    fileAttachmentCount: (id) => `${API_BASE_URL}/capa/${id}/file-attachment-count`
  },
  
  // User management endpoints
  users: {
    list: () => `${API_BASE_URL}/users`,
    byId: (id) => `${API_BASE_URL}/users/${id}`,
    create: () => `${API_BASE_URL}/users`,
    update: (id) => `${API_BASE_URL}/users/${id}`,
    delete: (id) => `${API_BASE_URL}/users/${id}`
  },
  
  // Training assignment endpoints
  trainingAssignment: {
    list: () => `${API_BASE_URL}/training-assignment`,
    byId: (id) => `${API_BASE_URL}/training-assignment/${id}`,
    create: () => `${API_BASE_URL}/training-assignment`,
    update: (id) => `${API_BASE_URL}/training-assignment/${id}`,
    delete: (id) => `${API_BASE_URL}/training-assignment/${id}`,
    relations: {
      users: () => `${API_BASE_URL}/training-assignment/relations/users`
    }
  },
  
  // Document management endpoints
  documents: {
    list: () => `${API_BASE_URL}/document-management/document`,
    byId: (id) => `${API_BASE_URL}/document-management/document/${id}`,
    create: () => `${API_BASE_URL}/document-management/document`,
    update: (id) => `${API_BASE_URL}/document-management/document/${id}`,
    delete: (id) => `${API_BASE_URL}/document-management/document/${id}`
  },
  
  // Application settings endpoints
  applicationSetting: {
    list: () => `${API_BASE_URL}/application-setting`,
    byId: (id) => `${API_BASE_URL}/application-setting/${id}`,
    byKey: (key) => `${API_BASE_URL}/application-setting/key/${key}`,
    create: () => `${API_BASE_URL}/application-setting`,
    update: (id) => `${API_BASE_URL}/application-setting/${id}`,
    delete: (id) => `${API_BASE_URL}/application-setting/${id}`
  }
};

export default {
  API_BASE_URL,
  API_ENDPOINTS
};
